import { Button, Modal as BootstrapModal, Form, ButtonGroup } from 'react-bootstrap';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import GenericTextArea from '../../../../controls/GenericTextArea';
import { useUIConfig } from '../Context';
import SearchBar from '../../../../controls/SearchBar';
import PubSub from '../../../../../services/PubSub';
import GenericCheckBox from '../../../../controls/GenericCheckbox';

const ExpressionBuilderModal = () => {
    const [translate] = useTranslation();

    const [selectedTab, setSelectedTab] = useState('functions');

    const [textToAdd, setTextToAdd] = useState('');
    const [initialTextValue, setInitialTextValue] = useState('');
    const [textValue, setTextValue] = useState('');
    const [warningInfo, setWarningInfo] = useState('');

    const [bcName, setBcName] = useState('');
    const [bcFieldsReadOnly, setBcFieldsReadOnly] = useState(false);
    const [bcFieldsData, setBcFieldsData] = useState([]);
    const [functionsData, setFunctionsData] = useState([]);
    const [translationsData, setTranslationsData] = useState([]);
    const [selectedTranslationFile, setSelectedTranslationFile] = useState('en-us');
    const [searchPrompt, setSearchPrompt] = useState('');
    const [miscData, setMiscData] = useState([]);

    const configList = useMemo(() => {
        return [
            { _id: "functions", displayValue: translate('OTM_FUNCTIONS') },
            { _id: "bcFields", displayValue: translate('OTM_BC_FIELDS') },
            { _id: "translations", displayValue: translate('OTM_TRANSLATION_STRINGS') },
            { _id: "misc", displayValue: translate('OTM_OTHERS') },
        ];
    }, [translate]);

    const { valuePrompterData, setValuePrompterData, translationFiles, translationsForFile, prompterFunctions, bcs, bcFields } = useUIConfig();

    const inputRef = useRef(null);
    const searchBarRef = useRef();

    useEffect(() => {
        setInitialTextValue(valuePrompterData.initialValue || '');
        setTextValue(valuePrompterData.initialValue || '');

        if (prompterFunctions) {
            setFunctionsData(prompterFunctions.sort((a, b) => a.value.localeCompare(b.value)));
        }

        setTranslationsData(translationsForFile(selectedTranslationFile));

        setMiscData([
            { displayValue: 'AND Operation [STATEMENT_1 && STATEMENT_2]', value: 'A && B' },
            { displayValue: 'OR Operation [STATEMENT_1 || STATEMENT_2]', value: 'A || B' },
            { displayValue: 'NOT Operation [!STATEMENT_1]', value: '!A' },
            { displayValue: 'EQUALS Operation [STATEMENT_1 === STATEMENT_2]', value: 'A === B' },
            { displayValue: 'IF STATEMENT [CONDITION_1 ? CASE_TRUE : CASE_FALSE]', value: 'A ? B : C' }
        ]);
        setWarningInfo('');
    }, [valuePrompterData, selectedTranslationFile, translationsForFile, prompterFunctions]);

    const handleSelectTab = useCallback((id) => {
        setSelectedTab(id);
        setSearchPrompt('');
        setBcName('');
        setTextToAdd('');
        if (searchBarRef.current) {
            searchBarRef.current.resetSearch();
        }
    }, []);

    const handleSelectBc = useCallback((value) => {
        const options = [];

        bcFields[value].forEach((attributeName) => {
            options.push({ value: attributeName, displayValue: attributeName, });
        });

        setBcFieldsData(options);
        setBcName(value);
    }, [bcFields])

    const updateValueAtCursor = useCallback((value, isBcName = false) => {
        if (selectedTab === 'bcFields' && !isBcName) {
            value = `${value}${bcFieldsReadOnly ? '.Value()' : '.RawValue()'}`;
        }

        if (inputRef.current.selectionStart === undefined) {
            setTextValue(prev => prev + value);
        }
        else {
            setTextValue(prev => `${prev.slice(0, inputRef.current.selectionStart)}${value}${prev.slice(inputRef.current.selectionEnd)}`);
        }

        setWarningInfo('');
    }, [selectedTab, bcFieldsReadOnly]);

    const validateBrackets = (text, bracketCharOpen, bracketCharClosed) => {
        var count = 0;
        for (var i = 0; i < text.length; i++) {
            if (text[i] === bracketCharOpen) {
                count++;
            }
            if (text[i] === bracketCharClosed) {
                count--;
            }

            if (count < 0) {
                return false;
            }
        }

        return count === 0;
    }

    const validateExpression = useCallback(() => {
        if (!validateBrackets(textValue, '(', ')')) {
            setWarningInfo(translate("OTM_WARNING_BRACKETS"));
            return false;
        }

        return true;
    }, [textValue, translate]);

    const onClose = useCallback(() => {
        setValuePrompterData((prev) => ({ ...prev, show: false }));
    }, [setValuePrompterData]);

    const onSave = useCallback(() => {
        if (!validateExpression() && !warningInfo) {
            PubSub.publish("show_info", "OTM_VALIDATION_ERROR", "OTM_WARNING");
            return;
        }

        valuePrompterData.updateCallback(valuePrompterData.configKey, valuePrompterData.paramName, textValue.replace(/(?:\r\n|\r|\n)/g, ''));
        setValuePrompterData((prev) => ({ ...prev, show: false }));
    }, [textValue, valuePrompterData, setValuePrompterData, warningInfo, validateExpression]);

    const renderAddButton = useCallback((isBcName = false) =>
        <Button
            size="sm"
            disabled={isBcName ? !bcName : !textToAdd}
            className='ms-auto d-flex'
            onClick={() => updateValueAtCursor(isBcName ? bcName : textToAdd, isBcName)}
        >
            {translate("OTM_ADD")}
        </Button>
        , [textToAdd, bcName, updateValueAtCursor, translate]);

    const renderSelectors = useMemo(() => {
        switch (selectedTab) {
            case 'functions': {
                return <div className="row mt-n2">
                    <div className="col mt-2">
                        <SearchBar
                            ref={searchBarRef}
                            size='sm'
                            initialValue={searchPrompt}
                            placeholder={translate("OTM_SEARCH")}
                            onChange={(value) => setSearchPrompt(value)} />
                    </div>
                    <div className='col-auto mt-2'>
                        {renderAddButton()}
                    </div>
                    <div className="col-12 mt-2">
                        <Form.Select size='sm' multiple style={{ height: '160px' }} onChange={(event) => setTextToAdd(event.target.value)}>
                            {functionsData.length === 0 && <option disabled={true}>{translate("OTM_NO_DATA_FOUND")}</option>}
                            {functionsData.filter(data =>
                                data.value.toLowerCase().includes(searchPrompt.toLowerCase()) ||
                                data.displayValue.toLowerCase().includes(searchPrompt.toLowerCase()))
                                .map((el, index) => <option key={index} value={el.value}>{el.displayValue}</option>)}
                        </Form.Select>
                    </div>
                    <div className="col-12 mt-2">
                        <GenericTextArea
                            type={"text"}
                            rows={3}
                            css={{ input: 'p-2 fs-7' }}
                            label={translate("OTM_DESCRIPTION")}
                            defaultValue={textToAdd !== '' ? functionsData.find(entry => entry.value === textToAdd).description : ''}
                            disabled={true}
                        />
                    </div>
                </div>
            }
            case 'bcFields': {
                return <>
                    <div className="row">
                        <div className="col-5">
                            <label className='form-label h6'>{translate('OTM_BCS')}</label>
                        </div>
                        <div className='col-1'>
                            {renderAddButton(true)}
                        </div>
                        <div className="col-5">
                            <label className='form-label h6'>{translate('OTM_BC_FIELDS')}</label>
                        </div>
                        <div className='col-1'>
                            {renderAddButton()}
                        </div>
                        <div className='col-6 mt-2'>
                            <Form.Select size='sm' multiple style={{ height: '200px' }} onChange={(event) => handleSelectBc(event.target.value)}>
                                {Object.keys(bcs).length === 0 && <option disabled={true}>{translate("OTM_NO_DATA_FOUND")}</option>}
                                {bcs && Object.keys(bcs).map((bc) => ({ value: bc, displayValue: bc })).map((el, index) => <option key={index} value={el.value}>{el.displayValue}</option>)}
                            </Form.Select>
                        </div>

                        <div className='col-6 mt-2'>
                            <Form.Select size='sm' multiple style={{ height: '200px' }} onChange={(event) => setTextToAdd(event.target.value)}>
                                {bcFieldsData.length === 0 && <option disabled={true}>{translate("OTM_NO_DATA_FOUND")}</option>}
                                {bcFieldsData.map((el, index) => <option key={index} value={el.value} selected={el.value === textToAdd}>{el.displayValue}</option>)}
                            </Form.Select>
                        </div>
                    </div >
                    <div className="mt-2 d-flex flex-row">
                        <GenericCheckBox
                            label={translate("OTM_IS_READONLY")}
                            initialValue={bcFieldsReadOnly}
                            onChange={(value) => setBcFieldsReadOnly(value)}
                            css={{ toggle: 'ms-2' }}
                        />
                    </div>
                </>
            }
            case 'translations': {
                const files = translationFiles();
                return <div className="row mt-n2">
                    <div className="col-12 mt-2">
                        <ButtonGroup>
                            {files && files.map(el => (
                                <Button
                                    key={el}
                                    variant={`${selectedTranslationFile === el ? "primary" : "secondary"}`}
                                    size="sm"
                                    onClick={() => {
                                        setSelectedTranslationFile(el);
                                        setTranslationsData(translationsForFile(el));
                                    }}
                                >
                                    {el}
                                </Button>
                            ))}
                        </ButtonGroup>
                    </div>
                    <div className="col mt-2">
                        <SearchBar
                            ref={searchBarRef}
                            size='sm'
                            initialValue={searchPrompt}
                            placeholder={translate("OTM_SEARCH_LANGUAGES")}
                            onChange={(value) => setSearchPrompt(value)} />
                    </div>
                    <div className="col-auto mt-2">
                        {renderAddButton()}
                    </div>
                    <div className="col-12 mt-2">
                        <Form.Select size='sm' multiple style={{ height: '200px' }} onChange={(event) => setTextToAdd(event.target.value)}>
                            {translationsData.length === 0 && <option disabled={true}>{translate("OTM_NO_DATA_FOUND")}</option>}
                            {translationsData.filter(data =>
                                data.value.toLowerCase().includes(searchPrompt.toLowerCase()) ||
                                data.displayValue.toLowerCase().includes(searchPrompt.toLowerCase()))
                                .map((el, index) => <option key={index} value={el.value}>{el.displayValue}</option>)}
                        </Form.Select>
                    </div>

                </div>
            }
            case 'misc': {
                return <div className="row mt-n2">
                    <div className="col mt-2">
                        <label className='form-label h6'>{translate('OTM_MISCELLANEOUS')}</label>
                    </div>
                    <div className="col-auto ms-auto mt-2">
                        {renderAddButton()}
                    </div>
                    <div className="col-12 mt-2">
                        <Form.Select size='sm' multiple style={{ height: '200px' }} onChange={(event) => setTextToAdd(event.target.value)}>
                            {miscData.length === 0 && <option disabled={true}>{translate("OTM_NO_DATA_FOUND")}</option>}
                            {miscData.map((el, index) => <option key={index} value={el.value}>{el.displayValue}</option>)}
                        </Form.Select>
                    </div>
                </div>
            }
            default: return;
        }
    }, [selectedTab, translationsData, bcFieldsData, functionsData, miscData, translationFiles, translationsForFile, selectedTranslationFile, bcs, searchPrompt,
        textToAdd, bcFieldsReadOnly, renderAddButton, handleSelectBc, translate]);

    const renderHeader = useMemo(() => {
        return <div className="row mt-n1">
            <div className="col-12 mt-1">
                {translate('OTM_EXPRESSION_BUILDER')} - [{valuePrompterData?.displayName}]
            </div>
            <div className="col-12 mt-1">
                <ul className="nav px-1 h6">
                    {
                        configList.map(item =>
                            <li
                                className={`border border-1 border-bottom-0 p-1 list-group-item clickable ${selectedTab === item._id ? "active" : ""}`}
                                onClick={() => handleSelectTab(item._id)}
                                key={item._id}
                            >
                                {item.displayValue}
                            </li>
                        )
                    }
                </ul>
            </div>
        </div>
    }, [valuePrompterData, selectedTab, configList, handleSelectTab, translate]);

    return (
        <BootstrapModal show={valuePrompterData?.show || false} size="xl" backdrop='static'>
            <BootstrapModal.Header className='h5 pb-0'>
                {renderHeader}
            </BootstrapModal.Header>
            <BootstrapModal.Body style={{ height: '500px' }} className="py-2">
                <div className="h-100 d-flex flex-column">
                    {renderSelectors}

                    <div className="mt-auto">
                        {warningInfo &&
                            <label className="text-accent mt-2 h6">{translate("OTM_WARNING")}: {warningInfo}</label>
                        }

                        <div className="row">
                            <div className="col">
                                <label className='form-label h6'>
                                    {valuePrompterData.displayName}
                                </label>
                                <div className="text-secondary" style={{ fontSize: '0.7em' }}>{valuePrompterData.description}</div>
                            </div>

                            <div className="col-auto">
                                <Button
                                    size="sm"
                                    disabled={initialTextValue === textValue}
                                    onClick={() => { setTextValue(initialTextValue); setWarningInfo(''); }}
                                >
                                    {translate("OTM_RESET_EXPRESSION")}
                                </Button>
                            </div>

                        </div>
                        <textarea
                            className='form-control form-control-sm disableShadow'
                            rows={4}
                            ref={inputRef}
                            value={textValue}
                            onChange={(event) => {
                                setTextValue(event.target.value);
                            }}
                            onBlur={(event) => { setTextValue(event.target.value); setWarningInfo(''); }}
                        />
                    </div>
                </div>
            </BootstrapModal.Body>
            <BootstrapModal.Footer>
                <Button onClick={onSave} variant='secondary' className='btn-sm'>{translate('OTM_SAVE')}</Button>
                <Button onClick={onClose} variant='secondary' className='btn-sm'>{translate('OTM_CLOSE')}</Button>
            </BootstrapModal.Footer>
        </BootstrapModal >
    );
};

export default ExpressionBuilderModal;
